/**
 * feedback 域 Zod 共享契约 — ADM-FBK-S001~S004
 */

import { z } from 'zod';

/**
 * 反馈附件 MIME 白名单 —— **`image/jpeg` / `image/png` 二值闭集**（dev-docs `03-数据字典/90-反馈.md` §1.5：
 * C 端与 admin 共用同一份，不设第二套）。`application/pdf` 已移出；HEIC / WebP 均不入。
 *
 * 前后端共用同一常量：前端图片选择器的 `accept` 与本地预拦截照它配，避免「前端放宽于服务端 = 选得进、发不出」。
 */
export const FEEDBACK_ATTACHMENT_CONTENT_TYPES = ['image/jpeg', 'image/png'] as const;
export type FeedbackAttachmentContentType = (typeof FEEDBACK_ATTACHMENT_CONTENT_TYPES)[number];

/** 附件大小上限 **10 MiB = 10485760 字节**（唯一精确常量：前端预拦截 / 服务端声明校验 / R2 实际对象校验共用）*/
export const FEEDBACK_ATTACHMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * 单条运营回复的附件张数上限 —— **1**（dev-docs `02-API契约/A7-运营反馈.md` §3，与 C 端对称；
 * 要发多张就发多条回复）。
 */
export const FEEDBACK_REPLY_MAX_ATTACHMENTS = 1;

/**
 * 附件 pre-create 请求体（ADM-FBK-S002，A7 §3.5）。字段语义与 C 端 §3.5 逐字相同、规格不设第二套。
 * `app_id` 不在 body —— 服务端取 `feedback.app_id`（工单 scoped，admin 无 X-App-Id 语义）。
 */
export const CreateFeedbackAttachmentRequestSchema = z.object({
  content_type: z.string().min(1),
  size_bytes: z.number().int(),
});
export type CreateFeedbackAttachmentRequest = z.infer<typeof CreateFeedbackAttachmentRequestSchema>;

/** 附件 pre-create 响应（A7 §3.5，形态与 C 端 §3.5 相同）*/
export type CreateFeedbackAttachmentResponse = {
  attachment_id: string;
  /** R2 PutObject **写** 签名 URL（10min TTL）。读一律走 §2.2 鉴权代理，本字段不含读权限 */
  signed_upload_url: string;
  signed_upload_url_expires_at: string;
};

export const ReplyFeedbackRequestSchema = z
  .object({
    /**
     * 回复正文。**可空**（dd#960 / 契约 dev-docs#969）——运营只发图片时传 `null` 或省略；
     * 非空时 1–5000 字符。
     */
    content: z.string().min(1).max(5000).nullish(),
    /**
     * 附件 id 数组，**最多 1 个**（A7 §3，与 C 端对称）。先调 `POST /feedback/:id/attachments`
     * pre-create + PUT 拿到 id 后引用；绑定时同时落 `feedback_id` 与 `reply_id`。
     */
    attachment_ids: z.array(z.string().uuid()).max(FEEDBACK_REPLY_MAX_ATTACHMENTS).default([]),
    /** 内部备注（仅运营可见，不发给用户）。**同样支持带图**（A7 §3）*/
    is_internal_note: z.boolean().default(false),
    /**
     * ADM-FBK-S003：勾选自动翻译。true 时 server 端把 content 译为该反馈提交语言（feedback.language → DeepL）
     * 后再入库（事务前译，失败抛 FEEDBACK_TRANSLATION_FAILED 不落库）；仅作用于非 internal_note 的用户可见回复。
     * **`content` 为空（纯图片回复）时本参数被忽略、不报错**——没有可译的文本（A7 §3）。
     */
    auto_translated: z.boolean().default(false),
  })
  .superRefine((val, ctx) => {
    // A7 §3 组合约束：`content` 与 `attachment_ids` **至少一者**非空、**可同时存在**（与 C 端「恰好一者」相反，
    // 两侧分别校验不共用口径）。两者皆空 → VALIDATION_FAILED 字段级校验，**不新造 FEEDBACK_* 业务码**。
    // 该约束不落 DB 守卫（附件在另一张表，跨表条件 CHECK 表达不了），由本 schema + 写事务共同保证。
    const hasContent = typeof val.content === 'string' && val.content.length > 0;
    if (!hasContent && val.attachment_ids.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['content'],
        message: 'content 与 attachment_ids 至少一者非空',
      });
    }
  });
export type ReplyFeedbackRequest = z.infer<typeof ReplyFeedbackRequestSchema>;

export type FeedbackListItem = {
  id: string;
  /** 无有效会话提交时无账号归属；设备关联账号不能补成工单所有者（A7 §1）。 */
  user_id: string | null;
  /** 已验证的提交设备快照；存量与 GDPR 清理后可空，仅用于客服排查。 */
  device_id: string | null;
  /** 同 App 设备历史关联账号；不代表工单归属，不赋予读取或回复收件人资格。 */
  related_device_users: { user_id: string; user_name: string | null }[];
  /**
   * 提交用户显示名（users.display_name，server left join 解析）。仅 admin 后台暴露，运营列表/详情展示真名
   * 而非 user_id UUID；C 端不暴露。display_name 可空（用户未设）→ null，前端回退 user_id 占位。
   */
  user_name: string | null;
  /**
   * 提交时确定的 App 强隔离键（feedback.app_id，FK→apps.id，不可变）。**仅 admin 后台**列表/详情暴露，
   * 运营按 App 维度筛选用（ADM-FBK-S001）；C 端 /me/feedback 不暴露（dev-docs §1 response 边界）。
   */
  app_id: string;
  /**
   * App 显示名（apps.name，server left join app_id 解析）。仅 admin 后台暴露，列表 chip / 详情头展示
   * 真实 App 名而非 UUID；理论恒有（app_id FK→apps.id），防御性 null（孤儿行）→ 前端回退 platform。
   */
  app_name: string | null;
  category: string;
  subject: string;
  /** 联系邮箱（server #189 邮件 opt-in 改可选）：未勾选邮件通知 → null，回复走 push + 站内通知 */
  contact_email: string | null;
  // dev-docs 03-数据字典/90-反馈.md：OPEN/IN_PROGRESS/REPLIED/RESOLVED/CLOSED 闭集
  status: 'OPEN' | 'IN_PROGRESS' | 'REPLIED' | 'RESOLVED' | 'CLOSED';
  platform: string;
  /**
   * 反馈提交语言（ISO 639-1，feedback.language）。admin 列表/详情暴露，运营据此核对 ADM-FBK-S003
   * auto_translated 回复的翻译目标语言；C 端 /me/feedback 不暴露（dev-docs §1 response 边界）。
   */
  language: string;
  last_reply_at: string | null;
  /**
   * 关闭来源（ADM-FBK-S004 自动关闭筛选）：取 feedback_status_log 最近一条 new_status='CLOSED' 的 actor_type
   * （'SYSTEM'=cron 自动关单 / 'ADMIN'=运营手动 / 'USER'=用户主动 close）；未关闭 / 无 CLOSED 流水 → null。
   * 纯读 C 端镜像列派生（不改 C 端 schema）。前端「自动关闭」tab 按 auto_closed_by==='SYSTEM' 筛选。
   */
  auto_closed_by: 'SYSTEM' | 'ADMIN' | 'USER' | null;
  created_at: string;
};

/**
 * ADM-FBK-S001 运营侧反馈内容中文翻译（server 侧调外部翻译 provider，结果缓存于 admin 独有表）。
 * 详情视图自动翻译 subject + description → 中文（DeepL 自动检测源语言）；feature 由 DEEPL_API_KEY 配置 gate。
 */
export type FeedbackTranslation = {
  /** 目标语言（运营固定中文）*/
  target_lang: string;
  /** 检测到的源语言（ISO 639-1，provider 返回；未知 null）*/
  source_lang: string | null;
  /** subject 译文（status=OK 时非空）*/
  subject: string | null;
  /** description 译文 */
  description: string | null;
  provider: string;
  /** OK = 译文有效；FAILED = 调用失败（优雅降级，详情仍返原文，运营看 FAILED 标记）*/
  status: 'OK' | 'FAILED';
};

export type FeedbackReplyResponse = {
  id: string;
  feedback_id: string;
  /**
   * 运营人员标识（server #188 Q1 改 nullable）：运营回复填、用户回复 NULL（与来源 is_from_support 配合）。
   * 用户回复（is_from_support=false）的 replied_by 为 null。
   */
  replied_by: string | null;
  /**
   * 回复来源（server #188 Q1）：运营回复=true / 用户回复=false。admin 据此区分线程双方。
   * optional：新 server 恒返；灰度窗旧 server 响应可能缺该字段 → 消费方按 `?? (replied_by != null)` 兜底推断来源。
   */
  is_from_support?: boolean;
  /**
   * 回复正文。**可空**（`02-API契约/A7-运营反馈.md` §3 表：`string | null`，「运营只发图片时传
   * `null` 或省略」）—— 纯附件回复是合法形态，DB 侧 `feedback_replies.content` 自 server #895
   * 起也已撤掉 NOT NULL、改为「NULL 或 1–5000 字」的表级 CHECK。渲染方必须处理 null。
   */
  content: string | null;
  is_internal_note: boolean;
  /** 仅运营详情展示的中文译文；旧接口可缺省，发送正文仍取 content。 */
  translation?: {
    target_lang: string;
    content: string | null;
    provider: string;
    status: 'OK' | 'FAILED';
  } | null;
  /**
   * 该条回复的附件（`reply_id = 本 reply.id` 且 `scan_status='CLEAN'` 的行）；无则 `[]`。
   * 与工单头部 `FeedbackDetailResponse.attachments`（提交时附件，`reply_id IS NULL`）**归属层级不同、
   * 项形态完全相同**（A7 §2.1）；`url` 同样指向 §2.2 鉴权代理端点。
   */
  attachments: FeedbackAttachmentItem[];
  email_sent_at: string | null;
  push_sent_at: string | null;
  created_at: string;
};

/**
 * 反馈状态流转单条（ADM-FBK 流转历史时间线）。读 C 端镜像表 feedback_status_log，按 created_at ASC。
 * admin 后台暴露全字段（含 actor_id / reason，运营责任追溯需要）——区别于 C 端 /me/feedback 时间线
 * （后者永不暴露 actor_id / reason，见 feedback-status-log.ts 文件头）。
 */
export type FeedbackStatusLogEntry = {
  id: string;
  old_status: string;
  new_status: string;
  /** 'ADMIN'（运营手动） / 'SYSTEM'（cron 自动关单） / 'USER'（用户主动 close）*/
  actor_type: 'ADMIN' | 'SYSTEM' | 'USER';
  /** admin 用户 id / 系统 cron 名 / users.id；可空 */
  actor_id: string | null;
  /** 切换原因（admin 后台填，可空）*/
  reason: string | null;
  created_at: string;
};

/**
 * 反馈用户附件预览项（ADM-FBK-S001 详情）— 源 C 端镜像 feedback_attachments（admin 只读）。
 * 仅 scan_status='CLEAN' 且未软删的附件暴露。**永不**回传 object_key / app_id（PII 边界）；
 * url 为鉴权代理读取地址（相对 ref，需 admin JWT）：GET /api/v1/feedback/:fid/attachments/:id
 * （admin 经 CONTENT_BUCKET binding 流式回传，不走公共 CDN / 不发公开签名 URL，对齐 dev-docs §1.5 PII 边界）。
 */
export type FeedbackAttachmentItem = {
  id: string;
  content_type: string;
  size_bytes: number;
  /**
   * PII 风险分级（运营预览前提示）：SCREENSHOT_GENERIC / CONTAINS_FACE / CONTAINS_DOCUMENT / UNKNOWN。
   * ⚠️ MVP 恒 `UNKNOWN`（dev-docs 90-反馈 §1.5：未引入 ML scan worker 前固定 UNKNOWN，运营后台**不依赖**本字段做
   * 脱敏决策）→ 前端 warn chip（含人脸/含证件）当前不显示，为未来 ML scan 分类预留（非 dead UI，是 forward-compat）。
   */
  pii_class: string;
  /** 鉴权代理读取地址（相对 ref，需 admin JWT）；前端 <img src>/下载用 */
  url: string;
};

export type FeedbackDetailResponse = FeedbackListItem & {
  app_version: string | null;
  device_info: Record<string, unknown> | null;
  /** 用户反馈正文（feedback.description，10–2000 字符必填；subject 仅短标题）*/
  description: string;
  replies: FeedbackReplyResponse[];
  /** ADM-FBK-S001 反馈内容中文翻译（subject + description）；feature 未启用（无 DEEPL_API_KEY）= null */
  translation: FeedbackTranslation | null;
  /** 状态流转历史（feedback_status_log，created_at ASC）；无流转 → [] */
  status_log: FeedbackStatusLogEntry[];
  /**
   * **提交时附件**（`reply_id IS NULL` 且仅 CLEAN，PII 边界）；无 → []。url 为鉴权代理地址。
   * 各轮回复的附件在 `replies[].attachments[]`，两处不可混为一谈（A7 §2.1）。
   */
  attachments: FeedbackAttachmentItem[];
};
